import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import Header from './Header'
import Footer from './Footer'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { format, subDays, isSameDay } from 'date-fns'
import { CalendarIcon, FileSpreadsheet, AlertTriangle, CheckCircle2, XCircle, Loader2, RefreshCw, Upload, ArrowLeft } from 'lucide-react'
import {
  fetchCustomers,
  fetchProducts,
  fetchProductTypes,
  createSaleWithItems,
  fetchConsignmentNumbers,
  fetchLatestRealSaleDate
} from '../supabase-client'
import { cn } from '@/lib/utils'

// API base URL - use relative path for Vercel, full URL for local dev
const API_BASE = import.meta.env.DEV ? 'http://localhost:3001' : ''

export default function GoogleSheetsImport({ user, onLogout }) {
  const navigate = useNavigate()

  // Data state
  const [preview, setPreview] = useState(null)
  const [records, setRecords] = useState([])
  const [customers, setCustomers] = useState([])
  const [products, setProducts] = useState([])
  const [productTypes, setProductTypes] = useState([])

  // UI state
  const [loading, setLoading] = useState(false)
  const [importing, setImporting] = useState(false)
  const [error, setError] = useState(null)
  const [importProgress, setImportProgress] = useState(0)
  const [importStatus, setImportStatus] = useState('')
  const [importResults, setImportResults] = useState(null)

  // Date range - will be set based on latest Odin date
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(new Date())
  const [latestOdinDate, setLatestOdinDate] = useState(null)

  // Mapping state
  const [customerMappings, setCustomerMappings] = useState({})
  const [productMappings, setProductMappings] = useState({})
  const [productTypeMappings, setProductTypeMappings] = useState({})

  // Load reference data on mount
  useEffect(() => {
    async function loadReferenceData() {
      try {
        const [customersData, productsData, productTypesData, latestDate] = await Promise.all([
          fetchCustomers(),
          fetchProducts(),
          fetchProductTypes(),
          fetchLatestRealSaleDate()
        ])
        setCustomers(customersData || [])
        setProducts(productsData || [])
        setProductTypes(productTypesData || [])

        // Set the latest Odin date and default start date to day after
        if (latestDate) {
          const odinDate = new Date(latestDate)
          setLatestOdinDate(odinDate)
          // Start date is the day after the latest Odin sale
          const dayAfter = new Date(odinDate)
          dayAfter.setDate(dayAfter.getDate() + 1)

          // Ensure start date doesn't exceed end date (today)
          const today = new Date()
          today.setHours(0, 0, 0, 0)
          dayAfter.setHours(0, 0, 0, 0)

          if (dayAfter > today) {
            // Latest sale is today or in the future - no new records to import
            setStartDate(today)
          } else {
            setStartDate(dayAfter)
          }
        } else {
          // No sales in Odin, default to 30 days ago
          setStartDate(subDays(new Date(), 30))
        }
      } catch (err) {
        console.error('Failed to load reference data:', err)
        setError('Failed to load reference data from database')
      }
    }
    loadReferenceData()
  }, [])

  // Build mappings when reference data or preview changes
  useEffect(() => {
    if (!preview || !customers.length || !products.length || !productTypes.length) return

    // Build customer mappings (match by name, case-insensitive)
    const custMap = {}
    const customersByName = {}

    // Build lookup including branches
    customers.forEach(c => {
      customersByName[c.name.toLowerCase()] = { customerId: c.id, branchId: null }
      if (c.branches) {
        c.branches.forEach(b => {
          // Map branch names to customer+branch
          customersByName[b.name.toLowerCase()] = { customerId: c.id, branchId: b.id }
          // Also try "customer - branch" format
          customersByName[`${c.name} - ${b.name}`.toLowerCase()] = { customerId: c.id, branchId: b.id }
        })
      }
    })

    // Special mappings for known customer name patterns
    const specialMappings = {
      'rf&f - fruit shop': { lookup: 'fruit shop' },      // Roots, Fruits & Flowers -> Fruit Shop branch
      'rf&f - kitchen': { lookup: 'kitchen' },            // Roots, Fruits & Flowers -> Kitchen branch
      'locavore partick': { lookup: 'partick' },          // Locavore -> Partick branch
      'locavore govanhill': { lookup: 'govanhill' },      // Locavore -> Govanhill branch
      'locavore vegbox': { lookup: 'vegbox' },            // Locavore -> VegBox branch
      'cottonrake cafe': { lookup: 'cottonrake cafe' },   // Cottonrake -> Cottonrake Cafe branch
    }

    preview.customers?.forEach(sheetCustomer => {
      const key = sheetCustomer.toLowerCase()

      // Check for special mapping first
      if (specialMappings[key]) {
        const lookupKey = specialMappings[key].lookup
        const match = Object.keys(customersByName).find(k => k.includes(lookupKey))
        if (match) {
          custMap[sheetCustomer] = customersByName[match]
          return
        }
      }

      // Try exact match
      if (customersByName[key]) {
        custMap[sheetCustomer] = customersByName[key]
      } else {
        // Try partial match
        const match = Object.keys(customersByName).find(k =>
          k.includes(key) || key.includes(k)
        )
        if (match) {
          custMap[sheetCustomer] = customersByName[match]
        }
      }
    })
    setCustomerMappings(custMap)

    // Build product mappings (match by strain name)
    const prodMap = {}
    const productsByStrain = {}
    products.forEach(p => {
      productsByStrain[p.strain.toLowerCase()] = p.id
      // Handle apostrophe variations
      productsByStrain[p.strain.toLowerCase().replace("'", '')] = p.id
    })

    preview.strains?.forEach(strain => {
      const key = strain.toLowerCase().replace("'", '')
      if (productsByStrain[key]) {
        prodMap[strain] = productsByStrain[key]
      } else if (productsByStrain[strain.toLowerCase()]) {
        prodMap[strain] = productsByStrain[strain.toLowerCase()]
      }
    })
    setProductMappings(prodMap)

    // Build product type mappings
    const typeMap = {}
    const typesByName = {}
    productTypes.forEach(t => {
      typesByName[t.name.toLowerCase()] = t.id
    })

    preview.productTypes?.forEach(typeName => {
      const key = typeName.toLowerCase()
      if (typesByName[key]) {
        typeMap[typeName] = typesByName[key]
      }
    })
    setProductTypeMappings(typeMap)

  }, [preview, customers, products, productTypes])

  // Fetch preview from API
  const fetchPreview = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch(`${API_BASE}/api/google-sheets?action=preview`)
      const data = await response.json()
      if (!response.ok) throw new Error(data.error || 'Failed to fetch preview')
      setPreview(data.preview)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  // Fetch records for import
  const fetchRecords = async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({
        action: 'fetch',
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd')
      })
      const response = await fetch(`${API_BASE}/api/google-sheets?${params}`)
      const data = await response.json()
      if (!response.ok) throw new Error(data.error || 'Failed to fetch records')
      setRecords(data.records || [])
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  // Calculate import readiness
  const getImportReadiness = useCallback(() => {
    if (!records.length) return { ready: 0, unmatched: 0, total: 0 }

    let ready = 0
    let unmatched = 0

    records.forEach(record => {
      const hasCustomer = !!customerMappings[record.customer]
      const hasProduct = !!productMappings[record.strain]
      const hasType = !!productTypeMappings[record.productType]

      if (hasCustomer && hasProduct && hasType) {
        ready++
      } else {
        unmatched++
      }
    })

    return { ready, unmatched, total: records.length }
  }, [records, customerMappings, productMappings, productTypeMappings])

  // Execute import
  const executeImport = async () => {
    setImporting(true)
    setImportProgress(0)
    setImportStatus('Preparing import...')
    setImportResults(null)

    try {
      // Get next consignment number
      // fetchConsignmentNumbers returns array of strings like ["CONS-2130", "CONS-2129", null, ...]
      const consignmentNumbers = await fetchConsignmentNumbers()
      const numericParts = consignmentNumbers
        .filter(s => s && typeof s === 'string' && s.startsWith('CONS-'))
        .map(s => parseInt(s.replace('CONS-', ''), 10))
        .filter(n => !isNaN(n))

      const maxNum = numericParts.length > 0 ? Math.max(...numericParts) : 0
      let nextConsignmentNum = maxNum + 1

      // Group records by date + customer + branch
      const groupedSales = {}
      records.forEach(record => {
        const customerMap = customerMappings[record.customer]
        const productId = productMappings[record.strain]
        const productTypeId = productTypeMappings[record.productType]

        // Skip unmapped records
        if (!customerMap || !productId || !productTypeId) return

        const key = `${record.isoDate}-${customerMap.customerId}-${customerMap.branchId || 'null'}`

        if (!groupedSales[key]) {
          groupedSales[key] = {
            date: record.isoDate,
            customerId: customerMap.customerId,
            branchId: customerMap.branchId,
            customerName: record.customer,
            items: []
          }
        }

        groupedSales[key].items.push({
          product_id: productId,
          product_type_id: productTypeId,
          quantity: record.quantity,
          price: record.price,
          packed: true
        })
      })

      const salesArray = Object.values(groupedSales)
      const totalSales = salesArray.length
      let created = 0
      let failed = 0
      const errors = []

      setImportStatus(`Importing ${totalSales} sales...`)

      for (const sale of salesArray) {
        try {
          await createSaleWithItems({
            date: sale.date,
            customer_id: sale.customerId,
            branch_id: sale.branchId,
            consignment_no: `CONS-${nextConsignmentNum++}`,
            status: 'delivered',
            items: sale.items
          })
          created++
        } catch (err) {
          failed++
          errors.push(`${sale.customerName} (${sale.date}): ${err.message}`)
        }

        setImportProgress(Math.round(((created + failed) / totalSales) * 100))
        setImportStatus(`Imported ${created} of ${totalSales} sales...`)
      }

      setImportResults({
        success: true,
        created,
        failed,
        errors,
        itemsImported: records.filter(r =>
          customerMappings[r.customer] &&
          productMappings[r.strain] &&
          productTypeMappings[r.productType]
        ).length
      })

    } catch (err) {
      setError(err.message)
      setImportResults({ success: false, error: err.message })
    } finally {
      setImporting(false)
      setImportProgress(100)
      setImportStatus('Import complete')
    }
  }

  // Initial load
  useEffect(() => {
    fetchPreview()
  }, [])

  const readiness = getImportReadiness()
  const unmatchedCustomers = preview?.customers?.filter(c => !customerMappings[c]) || []
  const unmatchedStrains = preview?.strains?.filter(s => !productMappings[s]) || []
  const unmatchedTypes = preview?.productTypes?.filter(t => !productTypeMappings[t]) || []

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header user={user} onLogout={onLogout} />

      <main className="flex-1 p-6 max-w-6xl mx-auto w-full">
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/sales')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Sales
          </Button>

          <div className="flex items-center gap-3">
            <FileSpreadsheet className="w-8 h-8 text-green-600" />
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Google Sheets Import</h1>
              <p className="text-gray-500">Import sales data from your Google Sheets Sales Record</p>
            </div>
          </div>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Connection Status */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Sheet Connection</CardTitle>
            <CardDescription>Connected to Sales Record spreadsheet</CardDescription>
          </CardHeader>
          <CardContent>
            {loading && !preview ? (
              <div className="flex items-center gap-2 text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                Loading sheet preview...
              </div>
            ) : preview ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Total Records</p>
                  <p className="text-2xl font-semibold">{preview.totalRecords?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Date Range</p>
                  <p className="text-sm font-medium">
                    {preview.dateRange?.earliest} to {preview.dateRange?.latest}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Unique Customers</p>
                  <p className="text-2xl font-semibold">{preview.uniqueCustomers}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Unique Products</p>
                  <p className="text-2xl font-semibold">{preview.uniqueStrains}</p>
                </div>
              </div>
            ) : (
              <Button onClick={fetchPreview} disabled={loading}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Connect to Sheet
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Mapping Status */}
        {preview && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Data Mapping Status</CardTitle>
              <CardDescription>
                Showing how sheet data maps to Odin database
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Customers */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Customers</span>
                  <span className="text-sm text-gray-500">
                    {Object.keys(customerMappings).length} / {preview.uniqueCustomers} mapped
                  </span>
                </div>
                <Progress
                  value={(Object.keys(customerMappings).length / preview.uniqueCustomers) * 100}
                  className="h-2"
                />
                {unmatchedCustomers.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {unmatchedCustomers.slice(0, 5).map(c => (
                      <Badge key={c} variant="outline" className="text-orange-600 border-orange-300">
                        {c}
                      </Badge>
                    ))}
                    {unmatchedCustomers.length > 5 && (
                      <Badge variant="outline">+{unmatchedCustomers.length - 5} more</Badge>
                    )}
                  </div>
                )}
              </div>

              {/* Products */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Products (Strains)</span>
                  <span className="text-sm text-gray-500">
                    {Object.keys(productMappings).length} / {preview.uniqueStrains} mapped
                  </span>
                </div>
                <Progress
                  value={(Object.keys(productMappings).length / preview.uniqueStrains) * 100}
                  className="h-2"
                />
                {unmatchedStrains.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {unmatchedStrains.map(s => (
                      <Badge key={s} variant="outline" className="text-orange-600 border-orange-300">
                        {s}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              {/* Product Types */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Product Types (Grades)</span>
                  <span className="text-sm text-gray-500">
                    {Object.keys(productTypeMappings).length} / {preview.uniqueProductTypes} mapped
                  </span>
                </div>
                <Progress
                  value={(Object.keys(productTypeMappings).length / preview.uniqueProductTypes) * 100}
                  className="h-2"
                />
                {unmatchedTypes.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {unmatchedTypes.map(t => (
                      <Badge key={t} variant="outline" className="text-orange-600 border-orange-300">
                        {t}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Up to Date Notice */}
        {latestOdinDate && isSameDay(latestOdinDate, new Date()) && (
          <Alert className="mb-6 border-green-300 bg-green-50">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertTitle>Odin is up to date</AlertTitle>
            <AlertDescription>
              The latest sale in Odin is from today ({format(latestOdinDate, 'PPP')}).
              There are no new records to import from the spreadsheet.
              If you need to re-import today's data, adjust the date range below.
            </AlertDescription>
          </Alert>
        )}

        {/* Date Range Selection */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Select Import Range</CardTitle>
            <CardDescription>
              {latestOdinDate
                ? `Latest sale in Odin: ${format(latestOdinDate, 'PPP')}. Only records after this date will be imported.`
                : 'Choose the date range of sales to import'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-center gap-4">
              <div>
                <label className="text-sm text-gray-500 block mb-1">Start Date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={cn("w-[200px] justify-start text-left font-normal")} disabled={!startDate}>
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate ? format(startDate, 'PPP') : 'Loading...'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={setStartDate}
                      disabled={(date) => latestOdinDate && date <= latestOdinDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <label className="text-sm text-gray-500 block mb-1">End Date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={cn("w-[200px] justify-start text-left font-normal")}>
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {endDate ? format(endDate, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={setEndDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="flex-1" />

              <Button onClick={fetchRecords} disabled={loading || !preview || !startDate}>
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Fetch Records
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Records Preview */}
        {records.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Records to Import</CardTitle>
              <CardDescription>
                {records.length} records found in selected date range
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-4 flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  <span>{readiness.ready} ready to import</span>
                </div>
                {readiness.unmatched > 0 && (
                  <div className="flex items-center gap-2">
                    <XCircle className="w-5 h-5 text-orange-500" />
                    <span>{readiness.unmatched} missing mappings (will be skipped)</span>
                  </div>
                )}
              </div>

              <div className="border rounded-lg overflow-hidden max-h-96 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Price</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {records.slice(0, 50).map((record, idx) => {
                      const hasCustomer = !!customerMappings[record.customer]
                      const hasProduct = !!productMappings[record.strain]
                      const hasType = !!productTypeMappings[record.productType]
                      const isReady = hasCustomer && hasProduct && hasType

                      return (
                        <TableRow key={idx} className={!isReady ? 'bg-orange-50' : ''}>
                          <TableCell>{record.date}</TableCell>
                          <TableCell className={!hasCustomer ? 'text-orange-600' : ''}>
                            {record.customer}
                          </TableCell>
                          <TableCell className={!hasProduct ? 'text-orange-600' : ''}>
                            {record.strain}
                          </TableCell>
                          <TableCell className={!hasType ? 'text-orange-600' : ''}>
                            {record.productType}
                          </TableCell>
                          <TableCell className="text-right">{record.quantity}</TableCell>
                          <TableCell className="text-right">£{record.price?.toFixed(2)}</TableCell>
                          <TableCell>
                            {isReady ? (
                              <Badge variant="outline" className="text-green-600 border-green-300">
                                Ready
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-orange-600 border-orange-300">
                                Missing
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>
              {records.length > 50 && (
                <p className="text-sm text-gray-500 mt-2">
                  Showing first 50 of {records.length} records
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Import Progress */}
        {importing && (
          <Card className="mb-6">
            <CardContent className="py-6">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Loader2 className="w-5 h-5 animate-spin text-green-600" />
                  <span className="font-medium">{importStatus}</span>
                </div>
                <Progress value={importProgress} className="h-2" />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Import Results */}
        {importResults && (
          <Alert className={importResults.success ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}>
            {importResults.success ? (
              <CheckCircle2 className="h-4 w-4 text-green-600" />
            ) : (
              <XCircle className="h-4 w-4 text-red-600" />
            )}
            <AlertTitle>{importResults.success ? 'Import Complete' : 'Import Failed'}</AlertTitle>
            <AlertDescription>
              {importResults.success ? (
                <div>
                  <p>Created {importResults.created} sales with {importResults.itemsImported} items.</p>
                  {importResults.failed > 0 && (
                    <p className="text-orange-600 mt-1">{importResults.failed} sales failed to import.</p>
                  )}
                </div>
              ) : (
                <p>{importResults.error}</p>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Import Button */}
        {records.length > 0 && readiness.ready > 0 && !importing && !importResults?.success && (
          <div className="flex justify-end">
            <Button
              size="lg"
              onClick={executeImport}
              className="bg-green-600 hover:bg-green-700"
            >
              <Upload className="w-4 h-4 mr-2" />
              Import {readiness.ready} Sales Records
            </Button>
          </div>
        )}
      </main>

      <Footer />
    </div>
  )
}
